import React, { useEffect, useState, useCallback } from "react";
import { Box, Typography, Button, TextField, InputAdornment } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import { useParams, useNavigate } from "react-router-dom";
import { useIntl } from "react-intl";
import { fetchPartnerFunctionsByCategory } from "../../api/companyApi";
import { useNotification } from "../../providers/NotificationProvider";

export default function PartnerFunctionListPage() {
  const { businessDomainId, categoryId } = useParams();
  const navigate = useNavigate();
  const intl = useIntl();
  const notify = useNotification();
  const t = (id) => intl.formatMessage({ id });

  const [rows, setRows] = useState([]);
  const [searchText, setSearchText] = useState("");

  const basePath = `/strategy/${businessDomainId}/partner-categories/${categoryId}`;

  const loadData = useCallback(async () => {
    try {
      const data = await fetchPartnerFunctionsByCategory(categoryId);
      setRows(Array.isArray(data) ? data.map((f) => ({ id: f.partnerFunctionId, ...f })) : []);
    } catch (e) { notify.apiError(e); }
  }, [categoryId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { loadData(); }, [loadData]);

  const filteredRows = searchText
    ? rows.filter((r) => r.partnerFunctionName?.toLowerCase().includes(searchText.toLowerCase()))
    : rows;

  const columns = [
    { field: "partnerFunctionName", headerName: t("partnerFunction.name"), flex: 1, minWidth: 200 },
    { field: "partnerFunctionDescription", headerName: t("partnerFunction.description"), flex: 1, minWidth: 200 },
    {
      field: "url",
      headerName: t("common.url"),
      flex: 1,
      minWidth: 200,
      renderCell: (params) =>
        params.value ? (
          <a href={params.value} target="_blank" rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}>
            {params.value}
          </a>
        ) : null,
    },
  ];

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Typography variant="h6">{t("partnerFunction.list.title")}</Typography>
        <TextField size="small" placeholder={t("common.search")} value={searchText} onChange={(e) => setSearchText(e.target.value)} InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} />
        <Button variant="contained" startIcon={<AddIcon />}
          onClick={() => navigate(`${basePath}/partner-functions/add`)}>
          {t("common.add")}
        </Button>
      </Box>

      <Box sx={{ height: 400 }}>
        <DataGrid
          rows={filteredRows}
          columns={columns}
          disableRowSelectionOnClick
          hideFooterSelectedRowCount
          onRowClick={(params) => navigate(`${basePath}/partner-functions/${params.row.partnerFunctionId}`)}
          sx={{ cursor: "pointer" }}
        />
      </Box>

      <Box sx={{ mt: 2 }}>
        <Button variant="outlined" onClick={() => navigate(basePath)}>{t("common.back")}</Button>
      </Box>
    </Box>
  );
}
